import {
  Alert,
  Attributes,
  Button,
  closeCurrentDialog,
  CreatableChoice,
  Label,
  openDialog,
  showNotification,
} from 'platform/components';
import {Box, HStack, Show, VStack} from 'platform/foundation';
import {formatPhoneNumber} from 'platform/locale';

import {head, isNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  PersonResponseBodyV2,
  useCreateCustomerContactV2Mutation,
  useGetCustomerV2Query,
  usePatchCustomerContactV2Mutation,
} from '@dms/api/customer';
import i18n from '@dms/i18n';

import {
  ApiError,
  buildArray,
  EMPTY_PLACEHOLDER,
  Nullish,
  suffixTestId,
  TestIdProps,
} from 'shared/web';

import {useCustomerPersonAttributes} from '../../hooks/useCustomerPersonAttributes';
import {getContactPersonPrimaryOptionFormatter} from '../../utils/getContactPersonPrimaryOptionFormatter';
import {getEmailsRequestBody} from '../../utils/getEmailsRequestBody';
import {getNaturalPersonFullName} from '../../utils/getNaturalPersonFullName';
import {handleContactApiError} from '../../utils/handleContactApiError';
import {ContactPersonForm} from '../ContactPersonForm/ContactPersonForm';
import {getContactPersonRequestBody} from './getContactPersonRequestBody';

type ContactPersonOption = {
  label: string;
  value: string;
};

interface ContactPersonSelectProps extends TestIdProps {
  customerId: string;
  persons: PersonResponseBodyV2[];
  selectedPersonId?: string | Nullish;
  isDisabled?: boolean;
  onSelect: (contactPersonId: string) => void;
}

const getPersonLabel = (person: PersonResponseBodyV2 | Nullish): string => {
  if (isNil(person)) {
    return EMPTY_PLACEHOLDER;
  }

  const fullName = getNaturalPersonFullName(person);
  if (fullName) {
    return fullName;
  }

  const phoneNumber = head(person.phoneNumbers ?? []);
  if (isNotNilOrEmpty(phoneNumber)) {
    return formatPhoneNumber(`${phoneNumber?.prefix}${phoneNumber?.number}`) ?? EMPTY_PLACEHOLDER;
  }

  const email = head(person.emails ?? []);
  if (isNotNilOrEmpty(email)) {
    return email?.email ?? EMPTY_PLACEHOLDER;
  }

  return EMPTY_PLACEHOLDER;
};

export function ContactPersonSelect(props: ContactPersonSelectProps) {
  const [createContact] = useCreateCustomerContactV2Mutation();
  const [patchContact] = usePatchCustomerContactV2Mutation();
  const [getAttributes] = useCustomerPersonAttributes();

  const {data: customer} = useGetCustomerV2Query({customerId: props.customerId});

  const primaryContactIds = new Set(buildArray<string>().whenNotNil(customer?.primaryContactId));

  const selectedPerson = props.persons.find((p) => p.id === props.selectedPersonId);

  const isSoftDeleted =
    isNotNilOrEmpty(props.selectedPersonId) &&
    !props.persons.some((p) => p.id === props.selectedPersonId);

  const options: ContactPersonOption[] = props.persons.map((person) => ({
    label: getPersonLabel(person),
    value: person.id,
  }));

  const handleChange = (value: string | null) => {
    if (isNil(value)) {
      return;
    }

    props.onSelect(value);
  };

  const handleCreate = () =>
    openDialog(
      <ContactPersonForm
        customerId={props.customerId}
        onSubmit={async (values, setErrors) => {
          const {emails, formIndexes} = getEmailsRequestBody(values.emails);

          await createContact({
            customerId: props.customerId,
            personRequestBody: {...getContactPersonRequestBody(values), emails},
          })
            .unwrap()
            .then((person) => {
              props.onSelect(person.id);
              showNotification.success(i18n.t('general.notifications.successfullyCreated'));
              closeCurrentDialog();
            })
            .catch((error: ApiError) => handleContactApiError(error, setErrors, formIndexes));
        }}
        data-testid={suffixTestId('createContact', props)}
      />,
      {
        title: i18n.t('entity.customer.actions.newContactPerson'),
        withAdditionalFooter: true,
        scrollBehavior: 'inside',
        'data-testid': suffixTestId('createContactDialog', props),
      }
    );

  const handleEdit = () => {
    if (!selectedPerson) {
      return;
    }

    openDialog(
      <ContactPersonForm
        customerId={props.customerId}
        contact={selectedPerson}
        onSubmit={async (values, setErrors) => {
          const {emails, formIndexes} = getEmailsRequestBody(values.emails);

          await patchContact({
            customerId: props.customerId,
            contactId: selectedPerson.id,
            personRequestBody: {...getContactPersonRequestBody(values), emails},
          })
            .unwrap()
            .then(() => {
              props.onSelect(selectedPerson.id);
              showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
              closeCurrentDialog();
            })
            .catch((error: ApiError) => handleContactApiError(error, setErrors, formIndexes));
        }}
        data-testid={suffixTestId('editContact', props)}
      />,
      {
        title: i18n.t('entity.customer.labels.editContactPerson'),
        withAdditionalFooter: true,
        scrollBehavior: 'inside',
        'data-testid': suffixTestId('editContactDialog', props),
      }
    );
  };

  return (
    <VStack spacing={3}>
      <Show when={isSoftDeleted}>
        <Alert
          data-testid={suffixTestId('softDeletedAlert', props)}
          variant="warning"
          title={i18n.t('general.notifications.contactPersonSoftDeleted')}
        />
      </Show>
      <HStack spacing={3}>
        <Box flexGrow={1}>
          <CreatableChoice
            value={props.selectedPersonId ?? null}
            options={options}
            formatOptionLabel={getContactPersonPrimaryOptionFormatter(primaryContactIds)}
            onChange={handleChange}
            onCreateOption={handleCreate}
            formatCreateLabel={(inputValue) => `${i18n.t('general.actions.create')} ${inputValue}`}
            isValidNewOption={() => true}
            label={i18n.t('entity.person.selectPerson')}
            isDisabled={props.isDisabled}
            isNotClearable
            data-testid={suffixTestId('select', props)}
          />
        </Box>
        <Show when={isNotNilOrEmpty(selectedPerson)}>
          <VStack justify="flex-end">
            <Label>&nbsp;</Label>
            <Button
              variant="ghostLink"
              leftIcon="image/edit"
              onClick={handleEdit}
              isDisabled={props.isDisabled}
              data-testid={suffixTestId('edit', props)}
              title={i18n.t('general.actions.edit')}
            />
          </VStack>
        </Show>
      </HStack>
      <Show when={selectedPerson}>
        {(selectedPerson) => (
          <Attributes
            data-testid={suffixTestId('personInfo', props)}
            rows={getAttributes(selectedPerson, {
              isRoleHidden: false,
              isEmailsHidden: false,
              isPhoneNumbersHidden: false,
              isPersonalIdentifierHidden: true,
            })}
            size="quarter"
          />
        )}
      </Show>
    </VStack>
  );
}
